import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ActivityFeedbackForm from './ActivityFeedbackForm';

const API_NODE_BASE = (process.env.REACT_APP_API_URL_NODE || 'http://localhost:3002').replace(/\/$/, '');

/* =========================
   Helpers
========================= */
const getOSMDetails = (feature) => {
    const id = feature.id || feature.properties?.['@id'];
    if (!id) return null;
    const match = String(id).match(/(node|way|relation)\/(\d+)/);
    if (!match) return null;
    return { type: match[1], id: match[2] };
};

const isCAIRelatedPoint = (feature) => {
    const p = feature?.properties || {};
    const candidates = [
        p.operator, p.owner, p.brand,
        p['operator:name'], p['owner:name'],
        p['operator:short'], p['owner:short']
    ].filter(Boolean).map(v => String(v).toLowerCase());

    const textMatch = candidates.some(v =>
        v.includes('club alpino') ||
        v.includes('c.a.i') ||
        (v.includes('cai') && v.includes('sezione'))
    );
    const wikidataMatch =
        p['operator:wikidata'] === 'Q237058' ||
        p['owner:wikidata'] === 'Q237058';

    return textMatch || wikidataMatch;
};

// Chiavi che contengono numeri di telefono
const PHONE_KEYS = new Set(['phone', 'mobile', 'fax', 'contact:phone', 'contact:mobile', 'contact:fax']);
// Chiavi che contengono email
const EMAIL_KEYS = new Set(['email', 'contact:email']);
// Chiavi che contengono URL
const URL_KEYS = new Set(['website', 'url', 'contact:website', 'contact:facebook',
    'contact:instagram', 'contact:twitter', 'contact:youtube']);

/* =========================
   Tracking helpers
========================= */
function trackContact(type, osmid, lat, lng) {
    // type = 'phone' | 'email'
    // fire-and-forget — non blocca mai l'apertura del client di posta/dialer
    const url = `${API_NODE_BASE}/go/${type}?osmid=${encodeURIComponent(osmid)}&lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
    fetch(url, { method: 'GET', keepalive: true })
        .then(r => console.log(`[OSM tracking] ${type} ok, status:`, r.status))
        .catch(err => console.warn(`[OSM tracking] ${type} error (non bloccante):`, err.message));
}

/* =========================
   TagTable (generica, solo testo)
========================= */
const TagTable = React.memo(({ tags }) => {
    const keys = useMemo(() => Object.keys(tags || {}).sort(), [tags]);
    if (!keys.length) return null;
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
                {keys.map(k => (
                    <tr key={k} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: 6, color: '#666', width: '40%' }}>{k}</td>
                        <td style={{ padding: 6 }}>{String(tags[k])}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
});

/* =========================
   ContactTagTable
   Rende phone → tel tracciato, email → mailto tracciato, url → <a> esterno
========================= */
const ContactTagTable = React.memo(({ tags, osmid, lat, lng }) => {
    const keys = useMemo(() => Object.keys(tags || {}).sort(), [tags]);
    if (!keys.length) return null;

    const handlePhone = useCallback((value) => {
        console.log('[OSM] Apertura tel:', value);
        trackContact('phone', osmid, lat, lng);
        window.location.href = `tel:${value}`;
    }, [osmid, lat, lng]);

    const handleEmail = useCallback((value) => {
        console.log('[OSM] Apertura mailto:', value);
        trackContact('email', osmid, lat, lng);
        window.location.href = `mailto:${value}`;
    }, [osmid, lat, lng]);

    const btnStyle = {
        background: 'none', border: 'none', padding: 0,
        fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'
    };

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
                {keys.map(k => {
                    const value = String(tags[k]);
                    let cell;

                    if (PHONE_KEYS.has(k)) {
                        cell = (
                            <button
                                style={{ ...btnStyle, color: '#2196F3' }}
                                onClick={() => handlePhone(value)}
                                title={`Chiama ${value}`}
                            >
                                {value}
                            </button>
                        );
                    } else if (EMAIL_KEYS.has(k)) {
                        cell = (
                            <button
                                style={{ ...btnStyle, color: '#2196F3' }}
                                onClick={() => handleEmail(value)}
                                title={`Scrivi a ${value}`}
                            >
                                {value}
                            </button>
                        );
                    } else if (URL_KEYS.has(k)) {
                        // URL: link esterno normale, nessun tracking necessario
                        const href = value.startsWith('http') ? value : `https://${value}`;
                        cell = (
                            <a href={href} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#2196F3' }}>
                                {value}
                            </a>
                        );
                    } else {
                        cell = value;
                    }

                    return (
                        <tr key={k} style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: 6, color: '#666', width: '40%' }}>{k}</td>
                            <td style={{ padding: 6 }}>{cell}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
});

/* =========================
   Component
========================= */
export default function OSMInfoDialog({ onClose, poi }) {
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);

    if (!poi) return null;

    const props = poi.properties || {};
    const coords = poi.geometry?.coordinates;
    const lat = coords?.[1];
    const lng = coords?.[0];

    // Estrai osmid pulito (es. "node/123" → "node/123" passato come stringa intera)
    const osmDetails = getOSMDetails(poi);
    const osmid = osmDetails
        ? `${osmDetails.type}/${osmDetails.id}`
        : (poi.id || poi.properties?.['@id'] || '');

    const exclude = ['id', 'geometry', 'bbox', 'poiCategory', 'poiVisualType'];

    const osmTags = {};
    const technicalTags = {};
    const contactTags = {};
    const addressTags = {};

    Object.keys(props).forEach(key => {
        if (exclude.includes(key)) return;
        const value = props[key];
        if (value === null || value === undefined || value === '') return;

        if (key.startsWith('@')) {
            technicalTags[key] = value;
        } else if (
            key.startsWith('contact:') ||
            PHONE_KEYS.has(key) ||
            EMAIL_KEYS.has(key) ||
            URL_KEYS.has(key) ||
            key === 'fax'
        ) {
            contactTags[key] = value;
        } else if (key.startsWith('addr:')) {
            addressTags[key] = value;
        } else {
            osmTags[key] = value;
        }
    });

    const isCAIPoint = isCAIRelatedPoint(poi);

    return (
        <>
            {showFeedbackForm && (
                <ActivityFeedbackForm poi={poi} onClose={() => setShowFeedbackForm(false)} />
            )}
            {createPortal(
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)', zIndex: 4000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                    }}
                    onClick={onClose}
                >
                    <div
                        style={{
                            background: 'white', borderRadius: '12px', padding: '24px',
                            maxWidth: '700px', width: '100%', maxHeight: '85vh', overflow: 'auto',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'start',
                            marginBottom: '16px', borderBottom: '3px solid #4CAF50', paddingBottom: '12px'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '22px', color: '#333' }}>
                                📍 {props.name || 'Informazioni OpenStreetMap'}
                            </h2>
                            <button onClick={onClose} style={{
                                background: 'none', border: 'none', fontSize: '28px',
                                cursor: 'pointer', color: '#666', padding: '0', lineHeight: '1'
                            }}>×</button>
                        </div>

                        <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#444' }}>

                            {/* Informazioni Principali */}
                            {Object.keys(osmTags).length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0', color: '#2196F3', fontSize: '16px',
                                        borderBottom: '2px solid #2196F3', paddingBottom: '6px'
                                    }}>📋 Informazioni Principali</h3>
                                    <TagTable tags={osmTags} />
                                </div>
                            )}

                            {/* Contatti — usa ContactTagTable per phone/email tracciati */}
                            {Object.keys(contactTags).length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0', color: '#4CAF50', fontSize: '16px',
                                        borderBottom: '2px solid #4CAF50', paddingBottom: '6px'
                                    }}>📞 Contatti</h3>
                                    <ContactTagTable
                                        tags={contactTags}
                                        osmid={osmid}
                                        lat={lat}
                                        lng={lng}
                                    />
                                </div>
                            )}

                            {/* Indirizzo */}
                            {Object.keys(addressTags).length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0', color: '#FF9800', fontSize: '16px',
                                        borderBottom: '2px solid #FF9800', paddingBottom: '6px'
                                    }}>📍 Indirizzo</h3>
                                    <TagTable tags={addressTags} />
                                </div>
                            )}

                            {/* Info Tecniche OSM */}
                            {Object.keys(technicalTags).length > 0 && (
                                <details style={{ marginBottom: '20px', cursor: 'pointer' }}>
                                    <summary style={{
                                        fontWeight: 'bold', padding: '8px', background: '#f5f5f5',
                                        borderRadius: '4px', userSelect: 'none', color: '#666'
                                    }}>🔧 Info Tecniche OSM</summary>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                                        <tbody>
                                            {Object.keys(technicalTags).sort().map(key => (
                                                <tr key={key} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                    <td style={{ padding: '8px 4px', width: '45%', color: '#999', fontSize: '12px' }}>
                                                        {key}:
                                                    </td>
                                                    <td style={{ padding: '8px 4px', width: '55%', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                                                        {String(technicalTags[key])}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </details>
                            )}

                            {/* Azioni OpenStreetMap */}
                            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '2px solid #eee' }}>
                                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                                    🔗 Azioni OpenStreetMap
                                </h3>

                                {/* ── CTA principale ── */}
                                <div style={{ marginBottom: 20, textAlign: 'center' }}>
                                    <button
                                        onClick={() => setShowFeedbackForm(true)}
                                        style={{
                                            width: '100%', padding: '14px 20px',
                                            background: '#2196F3', color: 'white',
                                            border: 'none', borderRadius: 8,
                                            fontSize: 16, fontWeight: 700, cursor: 'pointer',
                                            boxShadow: '0 4px 12px rgba(33,150,243,0.35)',
                                        }}
                                    >
                                        📩 Migliora queste informazioni
                                    </button>
                                    <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#888' }}>
                                        Nessuna registrazione richiesta
                                    </p>
                                    <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#555' }}>
                                        Le informazioni verranno verificate e aggiunte anche su OpenStreetMap
                                    </p>
                                </div>

                                {isCAIPoint ? (
                                    <>
                                        <div style={{
                                            marginBottom: '16px', padding: '12px',
                                            background: '#fff3e0', borderLeft: '3px solid #FF9800', borderRadius: '4px'
                                        }}>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#e65100', lineHeight: '1.5' }}>
                                                ⚠️ <strong>Segnavia gestito da CAI</strong><br />
                                                Questo punto fa parte di un percorso ufficiale. Le modifiche devono essere coordinate con l'ente gestore.
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <a href={`https://www.openstreetmap.org/note/new?lat=${lat}&lon=${lng}&text=${encodeURIComponent(`Segnalazione segnavia: ${props.name || 'punto di interesse'}`)}`}
                                                target="_blank" rel="noopener noreferrer"
                                                style={{
                                                    display: 'inline-block', padding: '12px 20px',
                                                    background: '#9C27B0', color: 'white', textDecoration: 'none',
                                                    borderRadius: '6px', fontSize: '14px', fontWeight: '600',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}>
                                                📝 Segnala problema
                                            </a>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                                        <a href={osmDetails
                                            ? `https://www.openstreetmap.org/edit?editor=id&${osmDetails.type}=${osmDetails.id}#map=18/${lat}/${lng}`
                                            : `https://www.openstreetmap.org/edit?editor=id#map=18/${lat}/${lng}`}
                                            target="_blank" rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-block', padding: '12px 20px',
                                                background: '#FF9800', color: 'white', textDecoration: 'none',
                                                borderRadius: '6px', fontSize: '14px', fontWeight: '600',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}>
                                            {osmDetails ? '✏️ Modifica su OSM (solo esperti)' : '✏️ Aggiungi a OSM (solo esperti)'}
                                        </a>

                                        <a href={`https://www.openstreetmap.org/note/new?lat=${lat}&lon=${lng}&text=${encodeURIComponent(`Segnalazione da AI Hiking per: ${props.name || 'punto di interesse'}`)}`}
                                            target="_blank" rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-block', padding: '12px 20px',
                                                background: '#9C27B0', color: 'white', textDecoration: 'none',
                                                borderRadius: '6px', fontSize: '14px', fontWeight: '600',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}>
                                            📝 Nota su OSM
                                        </a>
                                    </div>
                                )}

                                {/* Fallback email */}
                                <p style={{ textAlign: 'center', fontSize: 12, color: '#888', margin: '12px 0 4px 0' }}>
                                    Oppure scrivi a:{' '}
                                    <a href="mailto:info@aihiking.eu" style={{ color: '#2196F3' }}>info@aihiking.eu</a>
                                </p>

                                {/* Sezione donazioni */}
                                <div style={{
                                    marginTop: '20px', padding: '16px',
                                    background: 'linear-gradient(135deg, #fff9e6 0%, #fff3d6 100%)',
                                    borderRadius: '8px', border: '2px solid #ffd54f'
                                }}>
                                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666', fontWeight: '600', textAlign: 'center' }}>
                                        ☕ Aiuta a mantenere aggiornati i dati escursionistici
                                    </p>
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <a href="https://paypal.me/Giuliani434" target="_blank" rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                padding: '10px 16px', background: '#0070ba', color: 'white',
                                                textDecoration: 'none', borderRadius: '6px', fontSize: '13px',
                                                fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}>☕ PayPal</a>
                                        <a href="https://donate.stripe.com/4gM5kCbmceIvanaeBq5kk00" target="_blank" rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                padding: '10px 16px', background: '#635bff', color: 'white',
                                                textDecoration: 'none', borderRadius: '6px', fontSize: '13px',
                                                fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}>☕ Stripe</a>
                                    </div>
                                    <p style={{ margin: '12px 0 0 0', fontSize: '11px', color: '#999', textAlign: 'center' }}>
                                        I dati restano liberi e disponibili su OpenStreetMap
                                    </p>
                                </div>
                            </div>

                            <p style={{
                                fontSize: '11px', color: '#999', marginTop: '16px',
                                textAlign: 'center', paddingTop: '12px', borderTop: '1px solid #eee'
                            }}>
                                📊 Dati da OpenStreetMap
                            </p>
                        </div>

                        {/* Footer */}
                        <div style={{ marginTop: '24px', textAlign: 'right', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                            <button onClick={onClose} style={{
                                padding: '10px 20px', background: '#4CAF50', color: 'white',
                                border: 'none', borderRadius: '4px', cursor: 'pointer',
                                fontSize: '14px', fontWeight: '500'
                            }}>Chiudi</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
